import { Loader2 } from 'lucide-react'
import { Button } from '@/shared/components'
import type { ChangeEvent } from 'react'
import {
  formatAcceptedFormats,
  getUploadConfig,
  helperToneClasses,
  type UploadConfig
} from '../uploadDocumentConfig'
import type { VisibleUploadDocument } from './types'

type UploadDocumentCardProps = {
  document: VisibleUploadDocument
  index: number
  fileName: string
  isUploading: boolean
  mvrUrl: string
  onFileChange: (config: UploadConfig, event: ChangeEvent<HTMLInputElement>) => void
}

export function UploadDocumentCard({
  document,
  index,
  fileName,
  isUploading,
  mvrUrl,
  onFileChange
}: UploadDocumentCardProps) {
  const config = getUploadConfig(document)
  const tone = config.helperTone ? helperToneClasses[config.helperTone] : null
  const [helperBackground, helperBorder, helperTitleColor, helperTextColor] = tone
    ? tone.split(' ')
    : []

  return (
    <div
      className="animate-fade-in-up flex flex-col items-stretch gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:flex-row"
      style={{ animationDelay: `${120 + index * 80}ms` }}
    >
      <div className="flex-1">
        <p className="text-sm font-semibold uppercase tracking-[0.5px] text-primary">
          {config.title}
          {' *'}
        </p>
        <p className="mt-1 text-sm text-gray-600">{config.description}</p>
        <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-[8px] border border-dashed border-primary bg-primary/5 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10">
          <input
            type="file"
            className="hidden"
            accept={formatAcceptedFormats(document.acceptedFormats)}
            disabled={isUploading}
            onChange={event => {
              onFileChange(config, event)
            }}
          />
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : fileName ? (
            `✓ ${fileName}`
          ) : (
            config.uploadEmptyLabel
          )}
        </label>
      </div>

      {config.helperTitle && config.helperDescription && config.helperButtonLabel ? (
        <div
          className={`flex w-full shrink-0 flex-col items-center justify-center gap-2 rounded-xl border px-4 py-3 text-center md:w-[220px] ${helperBackground} ${helperBorder}`}
        >
          <p className={`text-sm font-bold ${helperTitleColor}`}>{config.helperTitle}</p>
          <p className={`text-sm ${helperTextColor}`}>{config.helperDescription}</p>
          <Button
            type="button"
            onClick={() =>
              config.key === 'mvr'
                ? window.open(mvrUrl, '_blank', 'noopener,noreferrer')
                : config.key === 'diploma'
                  ? window.open('https://www.needmytranscript.com/', '_blank', 'noopener,noreferrer')
                  : undefined
            }
            className="!text-sm !py-1 !px-3 hover:-translate-y-0.5 active:scale-95"
          >
            {config.helperButtonLabel}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
